import PropTypes from 'prop-types';

import {
  Route,
  Switch,
  withRouter,
} from 'core/libs/router';

import { isError4xx, setAppStatus } from 'core/utils/app-status-helper';

import {
  faq,
  main,
  tags,
  about,
  ai,
  topic,
  files,
  search,
  rubrics,
  contacts,
  notFound,
  error,
  topicAmp,
  rubricsAmp,
  tagsAmp,
  mainAmp,
  topicComments,
  authors,
  author,
  offline,
  leaderBoard,
} from './routes-patterns';

import { NOT_FOUND } from 'core/site/constants';

/**
 * Запрашиваем модули с помощью CommonJS, для того чтобы отработал jest.mock()
 *
 * Мокаем те модули, логика которых не важна в рамках тестирования роутинга.
 * При выполнении Jest, парсинг этих модулей впустую тратил бы ресурсы.
 */
import OfflinePage from 'core/components/OfflinePage';
import ErrorPage from 'core/components/ErrorPage';
import PrivateRoute from './PrivateRoute';

const routesParams = {
  faq,
  main,
  about,
  search,
  contacts,
};

/**
 * @author Evgeny Shiryaev <e.shiryaev@rambler-co.ru>
 *
 * @description
 * Компонент содержит базовый набор урлов для:
 * - `main`
 * - `tags`
 * - `rubrics`
 * - `ai`
 * - `topic`
 * - `topicAmp`
 * - `comments`
 * - `about`
 * - `contacts`
 * - `search`
 * - `legal`
 * - `rulesRecommenders`
 */
function BaseRoutes(props, context) {
  const {
    children,
    location,
    history,
    ...components
  } = props;

  const {
    features: {
      enableAiRubric,
    },
  } = context;

  const tagsList = Array.from(new Set(['label', ...props.tagsList]));

  /**
   * По документации `state` не будет приходить для `StaticRouter`.
   * Во избежание ошибок делаем обработку этого поведения.
   */
  const { appStatus } = location.state || props.staticContext || {};

  const simpleRoutes = Object
    .keys(components)
    .filter(name => routesParams[name])
    .map(name => (
      <Route
        {...routesParams[name]}
        component={components[name]}
        key={name}
      />
    ));

  function NotFound(params) {
    if (params.staticContext) {
      params.staticContext.status = NOT_FOUND;
      params.staticContext.appStatus = NOT_FOUND;
    }

    if (appStatus !== NOT_FOUND) {
      setAppStatus(history, NOT_FOUND);
    }

    if (components.notFound) {
      return (
        <components.notFound {...params} />
      );
    }

    return <ErrorPage />;
  }


  function ErrorComponent(params) {
    if (!appStatus) {
      setAppStatus(history, appStatus);
    }

    if (components.error) {
      return (
        <components.error {...params} />
      );
    }
    return (<ErrorPage
      type='error'
      errorCode={appStatus}
    />);
  }

  return (
    <Switch>
      <Route
        {...offline}
        component={OfflinePage}
      />
      {
        /**
          * Если статус приложения отрисованного на сервере соответствует
          * статусу `NOT_FOUND`, то сразу же делаем матч на компонент
          * страницы 404.
          *
          * В дальнейшем тут может добавляться обработка любых других статусов
          * приложения и они могут быть не завязаны на роутинг.
          */
      }
      {appStatus === NOT_FOUND && (
        <Route component={NotFound} />
      )}

      {process.env.BROWSER_RUNTIME && (!navigator.onLine || appStatus === 'offline') && (
        <Route component={OfflinePage} />
      )}

      {isError4xx(appStatus) && (
        <Route component={ErrorComponent} />
      )}

      {
        /**
          * Добавляем точный матч на отображение страницы 404.
          */
      }
      <Route
        {...notFound}
        component={NotFound}
      />
      <Route
        {...error}
        component={ErrorComponent}
      />
      {/**
        * Личный кабинет
        */}
      {components.profile && (
        <PrivateRoute
          path='/profile'
          component={components.profile}
        />
      )}

      {components.legal && (
        <Route
          path='/legal/:level2(terms-of-use|mailing-rules|privacy|terms-of-use-2)?'
          component={components.legal}
        />
      )}

      {components.rulesRecommenders && (
        <Route
          path='/rules-recommenders'
          component={components.rulesRecommenders}
        />
      )}

      {
        /**
          * Базовый набор роутов с точным совпадением (exact: true).
          */
      }
      {simpleRoutes}

      {
        /**
          * Если в url содержится фрагмент с `.htm/leaderboard` — это
          * страница рейтинга игрового сравнения.
          */
      }
      {components.leaderBoard && (
        <Route
          {...leaderBoard}
          component={components.leaderBoard}
        />
      )}

      {
        /**
          * Если в url содержится фрагмент с `.htm/comments` — это комментарии
          * статьи.
          *
          * При исправлении роута для комментариев не забыть поправить
          * обработку в дата-провайдере компонента `Ad`.
          */
      }
      {components.comments && (
        <Route
          {...topicComments}
          component={components.comments}
        />
      )}

      {components.mainAmp && (
        <Route
          {...mainAmp}
          component={components.mainAmp}
        />
      )}

      {
        /**
         * Если в url содержится фрагмент с `/amp/rubric_slug/topic_link.htm` - это amp статья
         */
      }
      {components.topicAmp && (
        <Route
          {...topicAmp}
          component={components.topicAmp}
        />
      )}

      {components.tagsAmp && (
        <Route
          {...tagsAmp}
          path={tagsAmp.path.replace('(label)', `(${tagsList.join('|')})`)}
          component={components.tagsAmp}
        />
      )}

      {components.rubricsAmp && (
        <Route
          {...rubricsAmp}
          component={components.rubricsAmp}
        />
      )}

      {
        /**
          * Если в url содержится фрагмент с `.htm` - это статья
          */
      }
      {components.topic && (
        <Route
          {...topic}
          component={components.topic}
        />
      )}

      {
        /**
          * Если в url содержится /ai, то выводятся топики,
          * сгенерированные ИИ.
          */
      }
      {enableAiRubric && components.ai && (
        <Route
          {...ai}
          component={components.ai}
        />
      )}

      {
        /**
          * Обрабатываем запросы за файлами. Это скорее всего паразитные
          * запросы от браузеров или поисковиков и они должны сразу
          * заворачиваться как 404.
          */
      }
      <Route
        {...files}
        component={NotFound}
      />

      {
        /**
          * Если url удовлетворяет условие `/authors`
          */
      }
      {components.authors && (
        <Route
          {...authors}
          component={components.authors}
        />
      )}

      {
        /**
          * Если url удовлетворяет условие `/authors/author_slug/`
          */
      }
      {components.author && (
        <Route
          {...author}
          component={components.author}
        />
      )}

      {
        /**
          * Если url удовлетворяет условие `/level_1/level_2/`` и `level_1`
          * совпадает с одним из типов тегов
          */
      }
      {components.tags && (
        <Route
          {...tags}
          path={tags.path.replace('(label)', `(${tagsList.join('|')})`)}
          component={components.tags}
        />
      )}

      {
        /**
          * После общих роутов выставляем пользовательские кастомные роуты.
          */
      }
      {children}

      {
        /**
          * Если `url` удовлетворяет условие паттерна `rubrics`,
          * не является тегом и `level_1` совпадает с одним из типов рубрик.
          */
      }
      {components.rubrics && (
        <Route
          {...rubrics}
          component={components.rubrics}
        />
      )}

      {
        /**
          * Если url не совпадает ни с одним из вышеперечисленных роутов,
          * то выставляем статус ответа 404 и выводим `components.notFound`.
          * В нашем случае это должна быть страница 404
          */
      }
      <Route component={NotFound} />
    </Switch>
  );
}

BaseRoutes.propTypes = {
  /**
   * Выводит определенный компонент, если location не совпадает ни с одним
   * из путей, которые указаны в других `<Route>`
   */
  notFound: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице "О сайте":<br/>`/about`
   */
  about: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице профиля:<br/>`/profile`
   */
  profile: PropTypes.func,

  /**
   * Выводит кастомный компонент, если URL совпадает с путем, указанным
   * в свойстве path
   */
  children: PropTypes.node,

  /**
   * Компонент, который нужно отобразить на странице контактов:<br/>`/contacts`
   */
  contacts: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице F.A.Q.:<br/>`/faq`
   */
  faq: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на главной странице нашего
   * приложения<br/>`/`
   */
  main: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице рубрики:<br/>
   * `(/{root_slug}/{rubric_slug})`
   */
  rubrics: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице поиска `/search`
   */
  search: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице топика:<br/>
   * `/topics/topic_link.htm`
   */
  topic: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице amp топика:<br/>
   * `/amp/topics/topic_link.htm`
   */
  topicAmp: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на главной странице в AMP:<br/>
   * `/amp/`
   */
  mainAmp: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице AMP тэга:<br/>
   * `/amp/tags/tag_slug`
   */
  tagsAmp: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице AMP рубрики:<br/>
   * `/amp/{root_slug}/{rubric_slug}`
   */
  rubricsAmp: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице комментариев топика:<br/>
   * `/topics/topic_link.htm/comments`
   */
  comments: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице тэга:<br/>
   * `/tags/tag_slug`
   */
  tags: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице авторов:<br/>
   * `/authors`
   */
  authors: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице автора:<br/>`/authors/author_slug`.
   */
  author: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице Правовая информация:<br/>
   * `/legal`.
   */
  legal: PropTypes.func,

  /**
   * Компонент, который нужно отобразить на странице Правила применения рекомендательных технологий:<br/>
   * `/rules-recommenders`.
   */

  rulesRecommenders: PropTypes.func,

  /**
   * Массив кастомных тегов, одно из значений которого может совпасть
   * с `level_1` маски `/:level_1(${tagsList.join('|')})/:level_2`.
   *
   * Объединится со значениями массива `['label']`
   */
  tagsList: PropTypes.arrayOf(PropTypes.string),

  /** @ignore */
  location: PropTypes.shape({
    state: PropTypes.shape({
      /**
       * Статус приложения с которым приложение было отрисовано на клиенте.
       *
       * По умолчанию это `undefined`.
       */
      appStatus: PropTypes.any,
    }),
  }).isRequired,

  /** @ignore */
  history: PropTypes.object,

  /** @ignore */
  staticContext: PropTypes.object,
};

BaseRoutes.contextTypes = {
  features: PropTypes.shape({
    enableAiRubric: PropTypes.bool,
  }),
};

BaseRoutes.defaultProps = {
  children: null,
  tagsList: [],
};

const BaseRoutesWithHOCs = withRouter(BaseRoutes);

BaseRoutesWithHOCs.displayName = 'BaseRoutes';

export { BaseRoutes as StorybookComponent };
export default BaseRoutesWithHOCs;
